import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Gift, Users, ListChecks, Sparkles } from "lucide-react";
import api from "../utils/api";
import { toast } from "sonner";
import { z } from "zod";

export default function Welcome() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);

  const emailSchema = z
    .string()
    .email("Bitte gib eine gültige E-Mail-Adresse ein");

  const handleSendMagicLink = async () => {
    if (!email) {
      toast.error(t("error"), { description: "Email is required" });
      return;
    }

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast.error(t("error"), { description: parsed.error.issues[0]?.message });
      return;
    }

    setLoading(true);
    await api.post("/auth/request-magic-link", {
      email,
      name: name || email.split("@")[0],
      language: "en",
    });
    toast.success(t("success"), { description: t("checkEmail") });
    setShowAuthDialog(false);
    try {
    } catch (error) {
      console.error("error:", error);
      toast.error(t("error"), {
        description:
          error.response?.data?.detail || "Failed to send magic link",
      });
    }
    setLoading(false);
  };

  const handleJoinSubmit = () => {
    if (inviteCode) {
      navigate(`/join/${inviteCode}`);
    }
  };

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-8 py-20">
        <div className="text-center mx-auto mb-4 space-y-4">
          <h1 className="text-3xl md:text-6xl text-rose-600 font-extrabold">
            {t("welcomeTitle")}
          </h1>
          <h2 className="text-xl md:text-3xl text-rose-400 font-bold">
            {t("welcomeSubtitle")}
          </h2>
        </div>
        <div className="gap-4 flex flex-col md:flex-row items-center justify-center">
          <Button
            data-testid="create-group-button"
            size="lg"
            className="bg-rose-600 hover:bg-rose-700 text-white px-8 py-6 text-lg rounded-full shadow-lg"
            onClick={() => setShowAuthDialog(true)}
          >
            <Gift className="mr-2 h-5 w-5" />
            {t("sendLogin")}
          </Button>
          <Button
            data-testid="join-group-button"
            size="lg"
            variant="outline"
            className="px-8 py-6 text-lg rounded-full shadow-lg"
            onClick={() => setShowJoinDialog(true)}
          >
            <Users className="mr-2 h-5 w-5" />
            {t("joinGroup")}
          </Button>
        </div>
      </div>

      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent data-testid="join-group-dialog">
          <DialogHeader>
            <DialogTitle>{t("joinGroup")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inviteCode">{t("inviteCode")}</Label>
              <Input
                data-testid="invite-code-input"
                id="inviteCode"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter invite code"
              />
            </div>
            <Button
              data-testid="join-submit-button"
              onClick={handleJoinSubmit}
              className="w-full"
            >
              {t("joinGroup")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle>{t("loginTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                data-testid="email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t("name")}</Label>
              <Input
                data-testid="name-input"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name"
              />
            </div>
            <Button
              data-testid="send-magic-link-button"
              onClick={handleSendMagicLink}
              className="w-full"
              disabled={loading}
            >
              {loading ? t("loading") : t("sendMagicLink")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
